import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, Phone, User, Car, Shield, CheckCircle2, XCircle, Sparkles, Download, FileText, Trash2 } from "lucide-react";

const STATUS = ["new", "reviewed", "contacted", "rejected"];
const STATUS_STYLES = {
  new: "bg-amber-100 text-amber-800",
  reviewed: "bg-blue-100 text-blue-800",
  contacted: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
};

const YES_NO_QUESTIONS = [
  ["authorized_and_meets_requirements", "Authorized to work in U.S. & meets base ID / background / access requirements"],
  ["has_real_id", "Has valid REAL ID"],
  ["food_safety_certification", "Valid ServSafe Food Handler certification"],
  ["comfortable_learning_pos", "Comfortable learning POS / new equipment"],
  ["sushi_raw_experience", "Sushi / raw-seafood experience"],
  ["reliable_transportation", "Reliable transportation"],
  ["clean_driving_record", "Clean driving record"],
  ["can_work_military", "Can work in military food court"],
  ["comfortable_food_safety", "Follows strict food-safety rules"],
  ["can_work_busy_hours", "Can work quickly during busy hours"],
  ["comfortable_taking_orders", "Comfortable with orders / register / customers"],
];

const TEXT_QUESTIONS = [
  ["food_service_experience", "Restaurant / food-service experience"],
  ["skill_to_improve", "Biggest strength & one area they're working to improve"],
  ["unsafe_food_handling_response", "If they saw unsafe food handling"],
  ["shift_arrival_time", "Arrival time for a 9 AM shift"],
  ["late_or_miss_shift_response", "If they're going to be late or miss a shift"],
  ["why_sandar_sushi", "Why they want to work at Sandar Sushi"],
];

function YesNoPill({ v }) {
  if (v === true) return <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold"><CheckCircle2 className="w-3.5 h-3.5"/>Yes</span>;
  if (v === false) return <span className="inline-flex items-center gap-1 text-red-600 text-xs font-semibold"><XCircle className="w-3.5 h-3.5"/>No</span>;
  return <span className="text-neutral-400 text-xs">—</span>;
}

// ---------------- Export helpers ----------------
const EXPORT_HEADERS = [
  ["created_at", "Received"],
  ["name", "Name"],
  ["email", "Email"],
  ["phone", "Phone"],
  ["current_location", "Current Location"],
  ["position", "Position"],
  ["work_type", "Work Type"],
  ["start_date", "Start Date"],
  ["years_experience", "Years Experience"],
  ["desired_hourly_salary", "Desired $/hr"],
  ["availability", "Availability"],
  ["past_employers", "Past Employers"],
  ["reference_name", "Reference Name"],
  ["reference_phone", "Reference Phone"],
  ...YES_NO_QUESTIONS,
  ...TEXT_QUESTIONS,
  ["status", "Status"],
];

const yesNo = (v) => v === true ? "Yes" : v === false ? "No" : "";

function cellValue(app, key) {
  const v = app[key];
  if (v === undefined || v === null) return "";
  if (typeof v === "boolean") return yesNo(v);
  if (key === "created_at") return new Date(v).toLocaleString();
  return String(v);
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function escapeCsv(v) {
  const s = String(v ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function exportCsv(apps) {
  const rows = [EXPORT_HEADERS.map(([, label]) => label).join(",")];
  for (const app of apps) {
    rows.push(EXPORT_HEADERS.map(([k]) => escapeCsv(cellValue(app, k))).join(","));
  }
  const blob = new Blob(["\uFEFF" + rows.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  triggerDownload(blob, `sandar-applications-${new Date().toISOString().slice(0,10)}.csv`);
}

function escapeHtml(v) {
  return String(v ?? "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
}

function exportWord(apps) {
  // Word-compatible HTML doc that opens in MS Word / Google Docs
  const style = `
    body { font-family: Calibri, Arial, sans-serif; color:#1a1a1a; }
    h1 { color:#EA580C; }
    h2 { border-bottom:2px solid #EA580C; padding-bottom:4px; margin-top:32px; }
    table { border-collapse: collapse; width: 100%; margin-top: 8px; }
    td, th { border: 1px solid #ddd; padding: 6px 10px; text-align: left; font-size: 12px; vertical-align: top; }
    th { background: #FEF3E5; }
    .meta { color: #6b7280; font-size: 12px; margin-bottom: 12px; }
    .section-title { color:#EA580C; font-weight:600; margin-top:14px; }
  `;
  const body = apps.map((app) => `
    <h2>${escapeHtml(app.name)} — ${escapeHtml(app.position)}</h2>
    <div class="meta">Received: ${escapeHtml(cellValue(app,"created_at"))} · Status: ${escapeHtml(app.status||"")}</div>
    <table>
      <tr><th style="width:220px">Field</th><th>Value</th></tr>
      ${EXPORT_HEADERS.slice(1).map(([k, label]) =>
        `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(cellValue(app,k))}</td></tr>`
      ).join("")}
    </table>
  `).join("");

  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>Sandar Sushi — Applications</title><style>${style}</style></head>
<body>
<h1>Sandar Sushi — Job Applications</h1>
<div class="meta">Exported ${new Date().toLocaleString()} · ${apps.length} application${apps.length===1?"":"s"}</div>
${body}
</body></html>`;
  const blob = new Blob(["\ufeff" + html], { type: "application/msword" });
  triggerDownload(blob, `sandar-applications-${new Date().toISOString().slice(0,10)}.doc`);
}

export default function CareersAdmin() {
  const [apps, setApps] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/careers/applications").then((r) => setApps(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/careers/applications/${id}/status`, { status }); load(); toast.success("Updated"); }
    catch { toast.error("Failed"); }
  };

  const deleteApp = async (a) => {
    if (!window.confirm(`Delete application from ${a.name}? This can't be undone.`)) return;
    try {
      await api.delete(`/careers/applications/${a.id}`);
      toast.success("Application deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-8 lg:p-10">
      <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow mb-2">Hiring</p>
          <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="admin-careers-title">Applications <span className="text-neutral-400 font-normal ml-2">({apps.length})</span></h1>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => exportCsv(apps)}
            disabled={apps.length === 0}
            className="rounded-xl"
            data-testid="export-csv-btn"
          ><Download className="w-4 h-4 mr-2"/>Export CSV</Button>
          <Button
            variant="outline"
            onClick={() => exportWord(apps)}
            disabled={apps.length === 0}
            className="rounded-xl"
            data-testid="export-word-btn"
          ><FileText className="w-4 h-4 mr-2"/>Export Word</Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-neutral-200/70 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="p-4">Applicant</th><th className="p-4">Position</th><th className="p-4">Contact</th>
              <th className="p-4">Received</th><th className="p-4">Status</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {apps.map((a) => (
              <tr key={a.id} data-testid={`app-row-${a.id}`}>
                <td className="p-4 font-medium">{a.name}<div className="text-xs text-neutral-500">{a.years_experience} yrs exp · ${a.desired_hourly_salary}/hr desired</div></td>
                <td className="p-4">{a.position}<div className="text-xs text-neutral-500 capitalize">{(a.work_type || "").replace("_"," ")}</div></td>
                <td className="p-4 text-neutral-600">{a.email}<div className="text-xs text-neutral-500">{a.phone}</div></td>
                <td className="p-4 text-neutral-600 text-xs">{new Date(a.created_at).toLocaleString()}</td>
                <td className="p-4">
                  <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
                    <SelectTrigger className="w-32 h-9" data-testid={`app-status-${a.id}`}><SelectValue/></SelectTrigger>
                    <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-4">
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => setSelected(a)} data-testid={`app-view-${a.id}`}>View</Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteApp(a)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                      data-testid={`app-delete-${a.id}`}
                    ><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div>
                </td>
              </tr>
            ))}
            {apps.length === 0 && (<tr><td colSpan={6} className="p-10 text-center text-neutral-500">No applications yet.</td></tr>)}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-3 text-2xl">
                {selected.name}
                <Badge className={STATUS_STYLES[selected.status]}>{selected.status}</Badge>
              </DialogTitle></DialogHeader>

              <div className="space-y-6 text-sm">
                {/* Contact */}
                <div className="grid grid-cols-2 gap-3">
                  <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-neutral-700 hover:text-[hsl(var(--primary))]"><Mail className="w-4 h-4 text-neutral-400"/>{selected.email}</a>
                  <a href={`tel:${selected.phone}`} className="flex items-center gap-2 text-neutral-700 hover:text-[hsl(var(--primary))]"><Phone className="w-4 h-4 text-neutral-400"/>{selected.phone}</a>
                </div>

                {/* Snapshot */}
                <div className="bg-neutral-50 rounded-xl p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><span className="text-neutral-500 text-xs">Position</span><div className="font-medium">{selected.position}</div></div>
                  <div><span className="text-neutral-500 text-xs">Desired</span><div className="font-medium">{selected.desired_hourly_salary ? `$${selected.desired_hourly_salary}/hr` : "—"}</div></div>
                  <div><span className="text-neutral-500 text-xs">Work type</span><div className="font-medium capitalize">{(selected.work_type || "—").replace("_", " ")}</div></div>
                  <div><span className="text-neutral-500 text-xs">Start</span><div className="font-medium">{selected.start_date || "—"}</div></div>
                  <div><span className="text-neutral-500 text-xs">Years</span><div className="font-medium">{selected.years_experience}</div></div>
                  <div><span className="text-neutral-500 text-xs">Location</span><div className="font-medium">{selected.current_location || "—"}</div></div>
                  <div className="col-span-2"><span className="text-neutral-500 text-xs">Availability</span><div className="font-medium">{selected.availability || "—"}</div></div>
                </div>

                {/* Yes/No grid */}
                <div>
                  <div className="text-xs text-neutral-500 uppercase tracking-wider mb-2 flex items-center gap-2"><Sparkles className="w-3 h-3"/>Screening answers</div>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 bg-white rounded-xl border border-neutral-200/70 p-4">
                    {YES_NO_QUESTIONS.map(([k, label]) => (
                      <div key={k} className="flex items-center justify-between text-sm border-b border-neutral-100 last:border-0 sm:[&:nth-last-child(2)]:border-0 py-1.5">
                        <span className="text-neutral-700 pr-3">{label}</span>
                        <YesNoPill v={selected[k]}/>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Past employers + reference */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Past employers</div>
                    <div className="text-neutral-800">{selected.past_employers || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Reference</div>
                    <div className="flex items-center gap-2 text-neutral-800"><User className="w-4 h-4 text-neutral-400"/>{selected.reference_name || "—"} <span className="text-neutral-500">· {selected.reference_phone || "—"}</span></div>
                  </div>
                </div>

                {/* Long-form answers */}
                <div className="space-y-4">
                  {TEXT_QUESTIONS.map(([k, label]) => (
                    <div key={k}>
                      <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1">{label}</div>
                      <div className="text-neutral-800 whitespace-pre-wrap bg-neutral-50 rounded-lg p-3 text-sm">{selected[k] || "—"}</div>
                    </div>
                  ))}
                  {selected.cover_note && (
                    <div>
                      <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Extra note</div>
                      <div className="text-neutral-800 whitespace-pre-wrap bg-neutral-50 rounded-lg p-3 text-sm">{selected.cover_note}</div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
